import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useNavigate } from 'react-router-dom'
import { Settings, LogOut, User } from 'lucide-react'

const ProfileContainer = styled.div`
  position: relative;
  display: inline-block;
`

const ProfileButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.border};
  background: ${props => props.$hasImage ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  padding: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.3);
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  left: auto;
  bottom: auto;
  width: 220px;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18), 0 4px 10px rgba(0,0,0,0.06);
  border: 1px solid ${props => props.theme.border};
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-8px'});
  transform-origin: top right;
  transition: all 0.18s ease;
`

const DropdownHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${props => props.theme.border};
`

const UserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 4px;
`

const UserEmail = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${props => props.theme.textPrimary};
  font-size: 14px;
  
  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }
  
  &:first-child {
    border-radius: 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
    border-top: 1px solid ${props => props.theme.border};
    color: #ff4757;
    
    &:hover {
      background-color: rgba(255, 71, 87, 0.1);
    }
  }
  
  svg {
    flex-shrink: 0;
  }
`

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`

export default function ProfileDropdown() {
  const { user, logout } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  const closeDropdown = () => {
    setIsOpen(false)
  }

  const handleSettings = () => {
    navigate('/settings')
    closeDropdown()
  }

  const handleLogout = () => {
    logout()
    navigate('/')
    closeDropdown()
  }

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen])

  // ESC 키로 드롭다운 닫기
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeDropdown()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
      return () => {
        document.removeEventListener('keydown', handleEscapeKey)
      }
    }
  }, [isOpen])

  if (!user) {
    return null
  }

  // 사용자 이름의 첫 글자를 아바타로 사용
  const getInitial = () => {
    if (user.full_name) {
      return user.full_name.charAt(0).toUpperCase()
    }
    if (user.name) {
      return user.name.charAt(0).toUpperCase()
    }
    if (user.username) {
      return user.username.charAt(0).toUpperCase()
    }
    return 'U'
  }

  return (
    <ProfileContainer ref={dropdownRef}>
      <Backdrop $isOpen={isOpen} onClick={closeDropdown} />

      <ProfileButton
        theme={theme}
        $hasImage={!!user.avatar_url}
        onClick={toggleDropdown}
        aria-label="프로필 메뉴"
        aria-expanded={isOpen}
      >
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="프로필" />
        ) : (
          getInitial()
        )}
      </ProfileButton>

      <DropdownMenu theme={theme} $isOpen={isOpen}>
        <DropdownHeader theme={theme}>
          <UserName theme={theme}>
            {user.full_name || user.name || user.username || '사용자'}
          </UserName>
          <UserEmail theme={theme}>
            {user.email || `${user.username || user.id}@example.com`}
          </UserEmail>
        </DropdownHeader>

        <DropdownItem theme={theme} onClick={handleSettings}>
          <Settings size={16} />
          계정 설정
        </DropdownItem>

        <DropdownItem theme={theme} onClick={handleLogout}>
          <LogOut size={16} />
          로그아웃
        </DropdownItem>
      </DropdownMenu>
    </ProfileContainer>
  )
}